'use client';

import {
  createContext,
  createElement,
  useContext,
  useRef,
  type ReactNode,
} from 'react';

import { z } from 'zod';

import { clearSignOutIntent, markSilentSignOut } from './auth-intent';
import { useSupabase } from './hooks/use-supabase';
import {
  IMPERSONATION_EXIT_SIGN_IN_PATH,
  IMPERSONATION_STALE_SIGN_IN_PATH,
} from './impersonation-path';

export const ImpersonationContext = createContext(false);

export function ImpersonationProvider({
  value,
  children,
}: {
  value: boolean;
  children: ReactNode;
}) {
  return createElement(ImpersonationContext, { value }, children);
}

export function useIsImpersonating() {
  return useContext(ImpersonationContext);
}

const ExitResponseSchema = z.object({
  redirectTo: z.literal(IMPERSONATION_EXIT_SIGN_IN_PATH),
});

export function useExitImpersonation() {
  const client = useSupabase();
  const destination = useRef<string | null>(null);

  return async () => {
    try {
      if (!destination.current) {
        const response = await fetch('/api/impersonation/exit', {
          method: 'POST',
        });

        if (response.status === 409 || response.status === 503) {
          // 409: the marker matched no session (the server cleared a stale
          // one). 503: the server could not read the session and kept the
          // marker. Either way end this browser's session only, never
          // globally.
          destination.current = IMPERSONATION_STALE_SIGN_IN_PATH;
        } else {
          if (!response.ok) throw new Error('Could not end impersonation');

          const result: unknown = await response.json();
          destination.current = ExitResponseSchema.parse(result).redirectTo;
        }
      }

      markSilentSignOut();
      const { error } = await client.auth.signOut({ scope: 'local' });

      if (error) throw error;

      window.location.replace(destination.current);
    } catch (error) {
      clearSignOutIntent();
      throw error;
    }
  };
}
